import styled from "@emotion/styled";
import type { RemindersUnavailableDeliveryOption } from "@/domains/metrics/tracked-event";
import {
  MDSButton,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSIcon,
  MDSIconKind,
  MDSModalDialog,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
} from "@/mds";

export interface RemindersComingSoonModalProps {
  isOpen: boolean;
  option: RemindersUnavailableDeliveryOption | null;
  isEnablingSlack?: boolean;
  onClose: () => void;
  onEnableSlack: () => void;
}

/**
 * Shows the waitlist modal for Reminders connectors that are not available yet.
 */
export const RemindersComingSoonModal = ({
  isOpen,
  option,
  isEnablingSlack = false,
  onClose,
  onEnableSlack,
}: RemindersComingSoonModalProps) => {
  if (!option) {
    return null;
  }

  if (option === "Teams") {
    return (
      <StyledModalDialog isOpen={isOpen} handleClose={onClose} fluidWidth>
        <StyledHeaderContent slot={MDSDialogSlotName.Header}>
          <MDSText as="h1" color={MDSTextColor.Contrast} variant={MDSTextVariant.HeaderL}>
            {"You're on the waitlist"}
          </MDSText>
        </StyledHeaderContent>

        <StyledContent slot={MDSDialogSlotName.Content}>
          <MDSText as="p" color={MDSTextColor.Primary} variant={MDSTextVariant.ParagraphS}>
            {
              "We’ll email you when Reminders in Teams are ready. In the meantime, you can enable reminders in iMessage, WhatsApp, or Slack."
            }
          </MDSText>
        </StyledContent>

        <StyledFooter slot={MDSDialogSlotName.Footer}>
          <MDSButton onClick={onClose} variant={MDSButtonVariant.Contrast}>
            Got it
          </MDSButton>
        </StyledFooter>
      </StyledModalDialog>
    );
  }

  return (
    <StyledModalDialog isOpen={isOpen} handleClose={onClose} fluidWidth>
      <StyledHeaderContent slot={MDSDialogSlotName.Header}>
        <MDSText as="h1" color={MDSTextColor.Contrast} variant={MDSTextVariant.HeaderL}>
          Reminders in {option} coming soon
        </MDSText>
      </StyledHeaderContent>

      <StyledContent slot={MDSDialogSlotName.Content}>
        <MDSText as="p" color={MDSTextColor.Primary} variant={MDSTextVariant.ParagraphS}>
          We&rsquo;ll email you when Reminders in {option} are ready. In the meantime, you can
          enable reminders in Slack.
        </MDSText>
      </StyledContent>

      <StyledFooter slot={MDSDialogSlotName.Footer}>
        <MDSButton onClick={onClose} variant={MDSButtonVariant.Default}>
          Close
        </MDSButton>
        <MDSButton
          onClick={onEnableSlack}
          variant={MDSButtonVariant.Contrast}
          isLoading={isEnablingSlack}
        >
          <MDSIcon kind={MDSIconKind.Slack} slot={MDSButtonSlotName.LeftIcon} />
          Enable Reminders in Slack instead
        </MDSButton>
      </StyledFooter>
    </StyledModalDialog>
  );
};

const modalContentWidth = "min(520px, calc(90vw - 56px))";

const StyledModalDialog = styled(MDSModalDialog)`
  .mds-dialog {
    gap: ${({ theme }) => theme.spacing["4"]};
  }
`;

const StyledHeaderContent = styled(MDSDialogTextContent)`
  && {
    padding-block: 0;
  }

  flex: 1 1 auto;
  min-width: 0;
`;

const StyledContent = styled.div`
  width: ${modalContentWidth};
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing["2"]};
  width: ${modalContentWidth};

  @container mds-panel (max-width: 520px) {
    flex-direction: column-reverse;
  }
`;
